/** Payload builders and validation for Customer, Supplier, Ship To, and Ship From. */

import { countryList } from "../../../data/static";
import { toPartnerApiStatus } from "../../../utils/partnerStatus";

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE = /^[\d+\-().\s]{7,20}$/;

export const STORER_COUNTRIES = countryList;

export const STORER_STATUS_OPTIONS = [
  { label: "Active", value: "Active" },
  { label: "Inactive", value: "Inactive" },
];

const text = (value) => String(value ?? "").trim();

/** Mongo id or partner code stored on the signed-in user (`partnerId`, then `partner_id`). */
export function loggedInPartnerId(user) {
  if (!user || typeof user !== "object") return "";
  const raw = user.partnerId ?? user.partnerDetails ?? user.partner ?? null;
  if (raw && typeof raw === "object") {
    return text(raw._id || raw.partner_id);
  }
  if (typeof raw === "string" && raw.trim()) return raw.trim();
  return text(user.partner_id);
}

/** Tenant mongo id on the signed-in user. */
export function loggedInTenantId(user) {
  const tenant = user?.tenantId;
  if (!tenant) return "";
  if (typeof tenant === "string") return tenant.trim();
  return text(tenant._id);
}

/**
 * Onboard body. Customer sends `customerId`; supplier sends `supplierId`.
 * Both ids are the fetched record's mongo `_id`.
 */
export function buildOnboardPayload(record, prefix, { partnerId = "", tenantId = "" } = {}) {
  const idField = prefix === "supplier" ? "supplierId" : "customerId";
  return {
    [idField]: text(record?._id || record?.id),
    partnerId: text(partnerId),
    tenantId: text(tenantId),
  };
}

/**
 * Disable body. Customer sends `customerId`; supplier sends `supplierId`.
 * Both ids are the list row's mongo `_id`, plus the signed-in partner and tenant.
 */
export function buildDisablePayload(record, prefix, ids) {
  return buildOnboardPayload(record, prefix, ids);
}

export function normalizeStorerStatus(value) {
  const v = text(value).toLowerCase();
  if (v === "inactive" || v === "3") return "Inactive";
  return "Active";
}

export function displayStorerStatus(value) {
  const n = toPartnerApiStatus(value, 1);
  if (n === 2) return "Pending";
  if (n === 3) return "Inactive";
  return "Active";
}

export function buildStorerPayload(form, prefix) {
  return {
    [`${prefix}_id`]: text(form?.partner_id),
    [`${prefix}_name`]: text(form?.partner_name),
    [`${prefix}_email`]: text(form?.partner_email),
    [`${prefix}_mobile`]: text(form?.partner_mobile),
    status: toPartnerApiStatus(form?.status, 1),
    address: {
      [`${prefix}_address1`]: text(form?.partner_address1),
      [`${prefix}_address2`]: text(form?.partner_address2),
      [`${prefix}_city`]: text(form?.partner_city),
      [`${prefix}_state`]: text(form?.partner_state),
      [`${prefix}_pincode`]: text(form?.partner_pincode),
      [`${prefix}_postCode`]: text(form?.partner_postCode),
      [`${prefix}_country`]: text(form?.partner_country),
    },
  };
}

export function validateStorerForm(form, label = "Customer") {
  const errors = {};
  const id = text(form?.partner_id);
  const name = text(form?.partner_name);
  const email = text(form?.partner_email);
  const mobile = text(form?.partner_mobile);

  if (!id) errors.partner_id = `${label} ID is required`;
  else if (id.length > 15) errors.partner_id = `${label} ID must be 15 characters or fewer`;

  if (!name) errors.partner_name = `${label} Name is required`;
  else if (name.length > 45) errors.partner_name = `${label} Name must be 45 characters or fewer`;

  if (!email) errors.partner_email = `${label} Email is required`;
  else if (!EMAIL.test(email)) errors.partner_email = "Please enter a valid email address";

  if (!mobile) errors.partner_mobile = "Contact Number is required";
  else if (!PHONE.test(mobile)) errors.partner_mobile = "Enter a valid contact number";

  if (!text(form?.partner_address1)) errors.partner_address1 = "Address Line 1 is required";
  if (!text(form?.partner_city)) errors.partner_city = "City is required";
  if (!text(form?.partner_state)) errors.partner_state = "State is required";
  if (!text(form?.partner_pincode)) errors.partner_pincode = "Pincode is required";
  if (!text(form?.partner_country)) errors.partner_country = "Country is required";

  return errors;
}

const toIdList = (value) => {
  if (Array.isArray(value)) return value.filter(Boolean);
  if (value == null || value === "") return [];
  return String(value).split(",").map((v) => v.trim()).filter(Boolean);
};

/**
 * Map a `customer-group` / `supplier` fetch row onto the shared create form.
 * Status stays the API code (`"1"` Active, `"2"` Pending, `"3"` Inactive).
 */
export function formStateFromStorerRecord(rec, prefix) {
  const flat = flattenStorerRecord(rec, prefix);
  const documents = Array.isArray(rec?.documents) ? rec.documents : [];
  const typeValue = rec?.[`${prefix}_type`];
  return {
    partner_id: flat.partner_id || "",
    partner_name: flat.partner_name || "",
    partner_email: flat.partner_email || "",
    partner_mobile: flat.partner_mobile || "",
    partner_type: typeValue != null && typeValue !== "" ? String(typeValue) : "",
    partner_noOfUserLicenses: flat.partner_noOfUserLicenses ?? "",
    bankName: flat["bank.bankName"] || "",
    ifscCode: flat["bank.ifscCode"] || "",
    accountNumber: flat["bank.accountNumber"] || "",
    partner_address1: flat["address.partner_address1"] || "",
    partner_address2: flat["address.partner_address2"] || "",
    partner_pincode: flat["address.partner_pincode"] || "",
    partner_postCode: flat["address.partner_postCode"] || "",
    partner_city: flat["address.partner_city"] || "",
    partner_state: flat["address.partner_state"] || "",
    partner_country: flat["address.partner_country"] || "",
    moduleAccess: toIdList(flat["moduleDetails.code"]),
    facilityId: toIdList(flat["facilityDetails.code"]),
    tenantId: flat.tenantId || "",
    users: rec?.createdBy || "",
    Partner_Documents: documents,
    acceptTerms: !!flat.acceptTerms,
    status: String(toPartnerApiStatus(rec?.status, 1)),
    username: flat.userDetails?.username || "",
    password: flat.userDetails?.password || "",
    confirmPassword: flat.userDetails?.password || "",
    operationalHours: hydrateOperationalHours(
      Array.isArray(rec?.operationalHours) ? rec.operationalHours : []
    ),
    documentList: documents.length
      ? documents.map((doc) => ({ docType: doc.docType || "", file: doc.file || null }))
      : [{ docType: "", file: null }],
  };
}

/** List row shape the shared partner table and edit form already read. */
export function flattenStorerRecord(rec, prefix) {
  const address = rec?.address || {};
  return {
    id: rec?._id,
    _id: rec?._id,
    partner_id: rec?.[`${prefix}_id`] || "",
    partner_name: rec?.[`${prefix}_name`] || "",
    partner_email: rec?.[`${prefix}_email`] || "",
    partner_mobile: rec?.[`${prefix}_mobile`] || "",
    "address.partner_address1": address[`${prefix}_address1`] || "",
    "address.partner_address2": address[`${prefix}_address2`] || "",
    "address.partner_city": address[`${prefix}_city`] || "",
    "address.partner_state": address[`${prefix}_state`] || "",
    "address.partner_pincode": address[`${prefix}_pincode`] || "",
    "address.partner_postCode": address[`${prefix}_postCode`] || "",
    "address.partner_country": address[`${prefix}_country`] || "",
    status: displayStorerStatus(rec?.status),
    acceptTerms: !!rec?.acceptTerms,
    "bank.bankName": rec?.bank?.bankName || "",
    "bank.ifscCode": rec?.bank?.ifscCode || "",
    "bank.accountNumber": rec?.bank?.accountNumber || "",
    documents: Array.isArray(rec?.documents) ? rec.documents : [],
    tenantId: rec?.tenantId || "",
    warehouse: rec?.warehouse || "",
    createdBy: rec?.createdBy || "",
    updatedBy: rec?.updatedBy || "",
    createdAt: rec?.createdAt || "",
    updatedAt: rec?.updatedAt || "",
    partner_noOfUserLicenses: rec?.[`${prefix}_noOfUserLicenses`] ?? "",
    "moduleDetails.code": Array.isArray(rec?.[`${prefix}_moduleAccess`])
      ? rec[`${prefix}_moduleAccess`]
      : Array.isArray(rec?.moduleAccess) ? rec.moduleAccess : "",
    "facilityDetails.code": Array.isArray(rec?.[`${prefix}_facilityId`])
      ? rec[`${prefix}_facilityId`]
      : Array.isArray(rec?.facilityId) ? rec.facilityId : "",
    operationalHours: rec?.[`${prefix}_operationalHours`] || rec?.operationalHours || "",
    userDetails: rec?.userDetails?.username
      ? rec.userDetails
      : { username: rec?.[`${prefix}_username`] || "", password: rec?.[`${prefix}_password`] || "" },
  };
}

/** Grid columns that stay off the table; the edit form still reads them from the row. */
export const STORER_ROW_ONLY_FIELDS = [
  "operationalHours",
  "partner_noOfUserLicenses",
  "userDetails",
  "moduleDetails.code",
  "facilityDetails.code",
  "documents",
];

export const OPERATIONAL_DAYS = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

export function emptyOperationalHours() {
  return OPERATIONAL_DAYS.reduce((acc, day) => {
    acc[day] = { enabled: false, open: "", close: "" };
    return acc;
  }, {});
}

/** API array `{ dayRange, timeRange }` → checkbox form state. */
export function hydrateOperationalHours(raw) {
  const hours = emptyOperationalHours();
  if (!Array.isArray(raw)) return hours;
  raw.forEach((item) => {
    const match = OPERATIONAL_DAYS.find(
      (day) => day.toUpperCase() === String(item?.dayRange || "").trim().toUpperCase()
    );
    if (!match) return;
    hours[match] = {
      enabled: true,
      open: item?.timeRange?.open || "06:00",
      close: item?.timeRange?.close || "23:00",
    };
  });
  return hours;
}

export function operationalHoursPayload(operationalHours = {}) {
  return Object.entries(operationalHours)
    .filter(([, data]) => data?.enabled)
    .map(([day, data]) => ({
      dayRange: day,
      timeRange: { open: data.open || "", close: data.close || "" },
    }));
}

export function operationalHoursApiBody(operationalHours = {}) {
  return operationalHoursPayload(operationalHours).map((row) => ({
    ...row,
    dayRange: String(row.dayRange || "").toUpperCase(),
  }));
}

/**
 * Full customer/supplier create body from the current swagger.
 * Sections that are not on the short form are taken from the partner create screen.
 */
export function buildStorerCreatePayload(
  form,
  prefix,
  { operationalHours = {}, documents = [], terms = "", includeOperationalHours = true } = {}
) {
  const payload = {
    ...buildStorerPayload(form, prefix),
    acceptTerms: !!form?.acceptTerms,
    terms: terms || "",
    userDetails: {
      username: text(form?.username),
      password: form?.password || "",
    },
    bank: {
      bankName: text(form?.bankName),
      ifscCode: text(form?.ifscCode),
      accountNumber: text(form?.accountNumber),
    },
    moduleAccess: Array.isArray(form?.moduleAccess) ? form.moduleAccess : [],
    facilityId: Array.isArray(form?.facilityId) ? form.facilityId : [],
    documents: Array.isArray(documents) ? documents : [],
  };
  if (includeOperationalHours) {
    payload.operationalHours = operationalHoursApiBody(operationalHours);
  }
  return payload;
}

export function validateStorerAccount(form, operationalHours = {}, { requireOperationalHours = true } = {}) {
  const errors = {};
  const username = text(form?.username);

  if (!Array.isArray(form?.moduleAccess) || form.moduleAccess.length === 0) {
    errors.moduleAccess = "Module Access is required";
  }

  if (!username) errors.username = "User name is required";
  else if (!EMAIL.test(username)) errors.username = "User name must be a valid email";

  if (!text(form?.password)) errors.password = "Password is required";
  if (!text(form?.confirmPassword)) errors.confirmPassword = "Confirm Password is required";
  else if (form.password !== form.confirmPassword) errors.confirmPassword = "Passwords do not match";

  if (!form?.acceptTerms) errors.acceptTerms = "Please accept Terms & Conditions";

  if (requireOperationalHours) {
    const openDays = Object.values(operationalHours).filter((day) => day?.enabled);
    if (openDays.length === 0) errors.operationalHours = "At least one operational day must be open";
  }

  return errors;
}

export function shipFieldDefs(prefix, label) {
  return [
    { name: `${prefix}_id`, label: `${label} ID`, required: true, maxLength: 15 },
    { name: `${prefix}_name`, label: `${label} Name`, required: true, maxLength: 45 },
    { name: `${prefix}_email`, label: "Email", required: true, type: "email" },
    { name: `${prefix}_mobile`, label: "Mobile", required: true },
    { name: `${prefix}_address1`, label: "Address Line 1", required: true },
    { name: `${prefix}_address2`, label: "Address Line 2" },
    { name: `${prefix}_city`, label: "City", required: true },
    { name: `${prefix}_state`, label: "State", required: true },
    { name: `${prefix}_pincode`, label: "Pincode", required: true },
    { name: `${prefix}_postCode`, label: "Post Code" },
    {
      name: `${prefix}_country`,
      label: "Country",
      required: true,
      type: "select",
      options: STORER_COUNTRIES,
    },
    { name: "status", label: "Status", type: "select", options: STORER_STATUS_OPTIONS },
  ];
}

export function emptyShipForm(prefix) {
  return {
    [`${prefix}_id`]: "",
    [`${prefix}_name`]: "",
    [`${prefix}_email`]: "",
    [`${prefix}_mobile`]: "",
    [`${prefix}_address1`]: "",
    [`${prefix}_address2`]: "",
    [`${prefix}_city`]: "",
    [`${prefix}_state`]: "",
    [`${prefix}_pincode`]: "",
    [`${prefix}_postCode`]: "",
    [`${prefix}_country`]: "",
    status: "Active",
  };
}

export function flattenShipRecord(rec, prefix) {
  const address = rec?.address || {};
  return {
    id: rec?._id,
    _id: rec?._id,
    [`${prefix}_id`]: rec?.[`${prefix}_id`] || "",
    [`${prefix}_name`]: rec?.[`${prefix}_name`] || "",
    [`${prefix}_email`]: rec?.[`${prefix}_email`] || "",
    [`${prefix}_mobile`]: rec?.[`${prefix}_mobile`] || "",
    [`${prefix}_address1`]: address[`${prefix}_address1`] || "",
    [`${prefix}_address2`]: address[`${prefix}_address2`] || "",
    [`${prefix}_city`]: address[`${prefix}_city`] || "",
    [`${prefix}_state`]: address[`${prefix}_state`] || "",
    [`${prefix}_pincode`]: address[`${prefix}_pincode`] || "",
    [`${prefix}_postCode`]: address[`${prefix}_postCode`] || "",
    [`${prefix}_country`]: address[`${prefix}_country`] || "",
    status: normalizeStorerStatus(rec?.status),
    operationalHours: Array.isArray(rec?.operationalHours)
      ? rec.operationalHours
      : Array.isArray(rec?.[`${prefix}_operationalHours`])
        ? rec[`${prefix}_operationalHours`]
        : [],
  };
}

export function buildShipPayload(
  form,
  { prefix, parentIdField, parentId, operationalHours, includeOperationalHours = false } = {}
) {
  const payload = {
    [`${prefix}_id`]: text(form?.[`${prefix}_id`]),
    [`${prefix}_name`]: text(form?.[`${prefix}_name`]),
    [`${prefix}_email`]: text(form?.[`${prefix}_email`]),
    [`${prefix}_mobile`]: text(form?.[`${prefix}_mobile`]),
    status: normalizeStorerStatus(form?.status),
    address: {
      [`${prefix}_address1`]: text(form?.[`${prefix}_address1`]),
      [`${prefix}_address2`]: text(form?.[`${prefix}_address2`]),
      [`${prefix}_city`]: text(form?.[`${prefix}_city`]),
      [`${prefix}_state`]: text(form?.[`${prefix}_state`]),
      [`${prefix}_pincode`]: text(form?.[`${prefix}_pincode`]),
      [`${prefix}_postCode`]: text(form?.[`${prefix}_postCode`]),
      [`${prefix}_country`]: text(form?.[`${prefix}_country`]),
    },
  };
  if (includeOperationalHours) {
    payload.operationalHours = operationalHoursApiBody(operationalHours);
  }
  if (parentIdField && parentId) payload[parentIdField] = parentId;
  return payload;
}

export function validateShipForm(form, prefix, label, { operationalHours, requireOperationalHours = false } = {}) {
  const errors = {};
  const id = text(form?.[`${prefix}_id`]);
  const name = text(form?.[`${prefix}_name`]);
  const email = text(form?.[`${prefix}_email`]);
  const mobile = text(form?.[`${prefix}_mobile`]);

  if (!id) errors[`${prefix}_id`] = `${label} ID is required`;
  else if (id.length > 15) errors[`${prefix}_id`] = `${label} ID must be 15 characters or fewer`;

  if (!name) errors[`${prefix}_name`] = `${label} Name is required`;
  else if (name.length > 45) errors[`${prefix}_name`] = `${label} Name must be 45 characters or fewer`;

  if (!email) errors[`${prefix}_email`] = "Email is required";
  else if (!EMAIL.test(email)) errors[`${prefix}_email`] = "Please enter a valid email address";

  if (!mobile) errors[`${prefix}_mobile`] = "Mobile is required";
  else if (!PHONE.test(mobile)) errors[`${prefix}_mobile`] = "Enter a valid mobile number";

  if (!text(form?.[`${prefix}_address1`])) errors[`${prefix}_address1`] = "Address Line 1 is required";
  if (!text(form?.[`${prefix}_city`])) errors[`${prefix}_city`] = "City is required";
  if (!text(form?.[`${prefix}_state`])) errors[`${prefix}_state`] = "State is required";
  if (!text(form?.[`${prefix}_pincode`])) errors[`${prefix}_pincode`] = "Pincode is required";
  if (!text(form?.[`${prefix}_country`])) errors[`${prefix}_country`] = "Country is required";

  if (requireOperationalHours) {
    const openDays = Object.values(operationalHours || {}).filter((day) => day?.enabled);
    if (openDays.length === 0) errors.operationalHours = "At least one operational day must be open";
  }

  return errors;
}
